/**
 * Похож ли адрес страницы на адрес товара.
 *
 * Живёт в общем пакете, потому что примета нужна обоим разборам, а разъехаться
 * им нельзя: по ней решается, карточка перед человеком или раздел каталога,
 * и разные ответы означали бы, что одна и та же ссылка ведёт себя по-разному
 * в зависимости от способа добавления.
 *
 * Примета сильная и дешёвая. Магазин обязан выдать товару опознаватель, иначе
 * на товар нельзя сослаться: артикул, код, номер модели. Разделу опознаватель
 * не нужен — у него имя. Замер по корпусу из 62 настоящих страниц: у сорока
 * из сорока одной карточки номер в адресе есть, а у всех разделов, которые
 * разбор принимал за карточку, его не было.
 */

/** Слова, которыми магазины называют раздел с товаром. */
const СЛОВО_ТОВАРА = /^(product|products|tovar|tovary|item|items|goods|detail|dp)$/i;

/** Кусок пути может быть закодирован, а может быть закодирован криво. */
function раскодировать(кусок: string): string {
  try {
    return decodeURIComponent(кусок);
  } catch {
    return кусок;
  }
}

/**
 * Кусок пути, НАЧИНАЮЩИЙСЯ словом «раздел»: «catalog--iz-za-rubezha».
 *
 * Так Яндекс Маркет обозначает категорию, и опознаватель у неё свой,
 * восьмизначный: /catalog--iz-za-rubezha/62307181. Из-за этого номера адрес
 * выглядел товарным, и разбор молча отдавал карточку — со случайной плиткой
 * из сетки. Я сходил по одной и той же ссылке трижды и получил два разных
 * товара: «Игрушка на тяговом механизме» за 95 ₽ и «Набор для ремонта шин»
 * за 560 ₽. Желание, которое меняется само по себе.
 *
 * Отличать надо именно СОСТАВНОЕ имя: обычный /catalog/obuv/12345 — это
 * товар внутри раздела, и трогать его нельзя. А «catalog--что-то» разделом
 * себя и называет.
 *
 * Замер по обоим корпусам: не задевает ни одной страницы.
 */
const НАЧИНАЕТСЯ_РАЗДЕЛОМ = /^(catalog|katalog|category|kategoriya|razdel|section|collection)[-_]{1,2}[a-z0-9]/i;

/**
 * Кусок пути, который ЦЕЛИКОМ есть слово «категория».
 *
 * Слово «category» отдельным куском — сильная примета раздела, в отличие
 * от «catalog»: каталог бывает и в товарных адресах (у ECCO /catalog/9108405/,
 * у обычного магазина /catalog/obuv/12345), а «category» так почти не
 * используют для одной вещи. Поймано на СтройДворе:
 * /ekb/category/stroitelnye-materialy-5521 — раздел стройматериалов, но
 * длинный кусок с номером 5521 делал адрес товарным, и разбор отдавал
 * карточку с названием магазина «Строительный двор» и ценой случайной плитки.
 *
 * «catalog» сюда НЕ входит намеренно — иначе поломались бы товарные адреса.
 * Слово должно быть куском ЦЕЛИКОМ: «/product/kategoriya-lyuks-nabor-777»
 * остаётся товаром.
 *
 * Замер по обоим корпусам: не задевает ни одной страницы.
 */
const КУСОК_ЭТО_КАТЕГОРИЯ = /^(category|categories|kategoriya|kategorii|rubrika|rubriki)$/i;

/**
 * Кусок пути, который ЦЕЛИКОМ есть слово про промо-кампанию: «actions»,
 * «promotions», «akcii». Страница акции — это подборка условий и товаров,
 * а не одна вещь, и без приметы её текст уезжал в название: у «Читай-города»
 * /promotions/54212, у «Планеты Здоровья» /actions/skidka-15-pri-1979/ — там
 * в поле «Что это» приезжало «Скидка 15% при покупке от 1979 ₽».
 *
 * «sale», «skidki», «rasprodazha» сюда НЕ входят намеренно: под ними живут
 * настоящие уценённые товары (/sale/krossovki-nike-123), и примета их бы
 * оболгала. Хаб кампании — другое: одной вещью он не бывает.
 *
 * Замер по обоим корпусам: не задевает ни одной страницы. И даже сработав,
 * примета лишь ставит пометку «раздел» — и только когда на странице нет
 * разметки одной вещи; настоящий товар с Product-разметкой защищён.
 */
const КУСОК_ЭТО_ПРОМО = /^(actions?|promotions?|promo|akci[iy]a?|akcia|aktsi[iy]a?|aktsia)$/i;

/**
 * Кусок пути, НАЧИНАЮЩИЙСЯ словом «акция»: «akcziya-marafon-bet-…».
 *
 * Тот же промо-хаб, но названный не отдельным куском, а прямо в имени
 * страницы. Поймано живым прогоном 08.08.2026 на Tiu:
 * /bookmaker-bonuses/akcziya-marafon-bet-privetstvennyj-bonus-do-25-000/ —
 * страница бонуса букмекера, и разбор отдавал её уверенной карточкой
 * (дов 0.95) с названием «Акция Марафон бет „Приветственный бонус до 25 000 ₽
 * за первое пополнение“» и ценой 25 000 ₽, БЕЗ единого предупреждения.
 * Товарным адрес делало общее правило про длинный кусок с цифрами: в имени
 * акции есть и то и другое.
 *
 * Почему это отдельная примета, а не строка в списке выше. Тот список требует
 * слово ЦЕЛИКОМ куском, и правильно требует: иначе «/product/kategoriya-lyuks»
 * стал бы разделом. Здесь же слово стоит первым в составном имени и кончается
 * дефисом — так называют саму кампанию, а не вещь. «Акция» в имени ВЕЩИ
 * встречается иначе: «nabor-po-akcii», «kofe-akciya-2-po-cene-1» — там слово
 * внутри или в конце, и примета их не берёт.
 *
 * Написаний несколько, потому что транслитерируют магазины кто как: у Tiu
 * «akcziya», встречаются «akciya» и «aktsiya». Рядом стоит только «promokod» —
 * тоже имя кампании, а не вещи.
 *
 * Английских «action-» и «promo-» здесь намеренно НЕТ, и это не осторожность
 * вообще, а конкретный товар: «action-camera-gopro-11-123456» — экшн-камера,
 * настоящая вещь, и такая проверка в наборе уже стоит. Начни примета брать
 * английские слова — она отняла бы опознаватель у неё. Отдельным куском
 * «promo» и «action» по-прежнему ловятся списком выше: там это хаб кампании,
 * а не начало имени вещи.
 *
 * Замер по обоим корпусам (197 страниц): не задевает ни одной. И даже сработав,
 * примета лишь ставит пометку «раздел» — и только там, где на странице нет
 * разметки одной вещи.
 */
const НАЧИНАЕТСЯ_ПРОМО = /^(akci[iy]a|akcia|akcziya|aktsi[iy]a|aktsia|promokod|promocode)[-_]/i;

/**
 * Кусок «filter» — это механизм фасетной фильтрации, а страница за ним —
 * отфильтрованный СПИСОК, а не одна вещь. Поймано на «Много Мебели»:
 * /krovati/filter/shirina-spalnogo-mesta--ot-180-do-180/ — подборка кроватей,
 * но длинный кусок с параметрами делал адрес товарным, и в поле «Что это»
 * приезжало имя из мета-заголовка «ООО „Много Мебели“».
 *
 * Только английское «filter» и целиком куском: русский товар-фильтр пишут
 * «filtr» («/product/filtr-maslyanyy-123»), а множественное «filtry» — это
 * категория фильтров, что тоже не одна вещь. Замер по обоим корпусам: 0.
 */
const КУСОК_ЭТО_ФИЛЬТР = /^filter$/i;

export function адресПохожНаТовар(адрес: string | URL | null | undefined): boolean {
  if (!адрес) return true; // Судить не по чему — и запрещать не за что.

  let путь: string;
  try {
    путь = (typeof адрес === 'string' ? new URL(адрес) : адрес).pathname;
  } catch {
    return true;
  }

  const куски = путь.split('/').filter(Boolean).map(раскодировать);
  if (куски.length === 0) return false;

  /*
   * Слово «товар» в адресе считается опознавателем, только если за ним стоит
   * номер.
   *
   * Иначе оно ловит дерево каталога: у Doctorhead адрес раздела —
   * /brands/loewe/products/personal-audio/naushniki/polnorazmernye/, и слово
   * «products» там такая же ветка, как «brands». У Pult.ru то же самое:
   * /product/uslugi/kompleksnye-resheniya/ — это раздел услуг.
   *
   * У настоящего товара за словом идёт его номер: /product/73872/…,
   * /product/index/id/3139136/, /zhenskaya/product/2136302113/99.
   */
  // Кусок, назвавший себя разделом, перевешивает любой номер дальше по пути.
  if (куски.some((кусок) => НАЧИНАЕТСЯ_РАЗДЕЛОМ.test(кусок))) return false;
  if (куски.some((кусок) => КУСОК_ЭТО_КАТЕГОРИЯ.test(кусок))) return false;
  if (куски.some((кусок) => КУСОК_ЭТО_ПРОМО.test(кусок))) return false;
  if (куски.some((кусок) => НАЧИНАЕТСЯ_ПРОМО.test(кусок))) return false;
  if (куски.some((кусок) => КУСОК_ЭТО_ФИЛЬТР.test(кусок))) return false;

  const словоТовара = куски.findIndex((кусок) => СЛОВО_ТОВАРА.test(кусок));
  if (словоТовара >= 0 && куски.slice(словоТовара + 1).some((кусок) => /\d/.test(кусок))) return true;

  /*
   * Слово «товар» плюс длинное имя из нескольких слов через дефис.
   *
   * Карточки без номера в адресе существуют, и до этого правила они считались
   * не-товарными: «/product/aroma-box-essential» у randewoo,
   * «/product/ofisnoe-kreslo-dejzi-bucle-terra» у Диван.ру. Расплачивались
   * они предупреждением «похоже, это раздел каталога» — на настоящей карточке.
   *
   * Требование двух дефисов и пятнадцати знаков — не подгонка, а именно то,
   * что отличает имя вещи от имени раздела. Раздел называют одним-двумя
   * словами: «uslugi», «kompleksnye-resheniya» у Doctorhead — один дефис.
   * Вещь называют перечислением примет: что это, какое, какого цвета.
   *
   * Замер по обоим корпусам: товарным стал ровно один адрес — карточка
   * Диван.ру, — и ни один раздел.
   */
  if (
    словоТовара >= 0 &&
    куски.slice(словоТовара + 1).some((кусок) => кусок.length >= 15 && (кусок.match(/-/g) ?? []).length >= 2)
  ) {
    return true;
  }

  /*
   * Длинный кусок пути считается опознавателем, только если в нём есть цифра.
   *
   * Без этой оговорки правило ловило не товары, а русский язык. Название
   * раздела в транслите длинное само по себе: «poncho-turisticheskie» — 21
   * знак, «demisezonnye_zhenskie_shorty» — 28, «elektronika_dlya_avtomobilya» —
   * тоже 28. Ровно на этом пять разделов корпуса и проходили за карточку,
   * а человек получал желание с названием раздела и ценой случайного товара
   * из сетки.
   *
   * Замер по корпусу: оговорка отняла опознаватель у пяти разделов и не задела
   * ни одной из 41 карточки. А карточки, где номера нет вовсе, — например
   * «/product/ofisnoe-kreslo-dejzi-bucle-terra» у Диван.ру — опознаются словом
   * «product» правилом выше.
   */
  return куски.some((кусок) => {
    /*
     * Голое число опознаватель ТОВАРА, только если оно длинное.
     *
     * Раздел тоже носит номер: у Регарда «/catalog/1536/komplektuyushchie-dlya-pk»
     * число 1536 — это id раздела, и правило «четыре цифры в куске» читало
     * его как товар. Человек получал желание «Комплектующие для ПК» с ценой
     * случайной железки из сетки.
     *
     * Отличает длина. Номера разделов короткие (1536), номера товаров длинные:
     * у ECCO 9108405, у Московского дома книги 1266413, у Олди 02157537 —
     * семь-восемь цифр. Порог в пять цифр разделяет их и, по замеру
     * на обоих корпусах, не отнимает опознаватель ни у одной карточки.
     *
     * Кусок, где цифры вперемешку с БУКВАМИ, — это имя с артикулом
     * («kreslo-1194821345»), и он остаётся опознавателем при любой длине
     * числа: раздел так не называют.
     */
    const этоГолоеЧисло = /^\d+$/.test(кусок);
    if (этоГолоеЧисло) return кусок.length >= 5;
    return (кусок.length >= 20 && /\d/.test(кусок)) || (кусок.match(/\d/g) ?? []).length >= 4;
  });
}

/**
 * Раздел адреса, которым магазин обозначает чтиво, а не товар.
 *
 * Жил в разборе браузера, перенесён в общий пакет по прямому замеру: из пяти
 * страниц корпуса, которые сервер ещё принимал за товар, три отличаются
 * ровно этим — обзор Аудиомании по /content/, «интересные факты» Эксмо
 * по /articles/, промо-страница букмекера у Tiu. Браузерный разбор их
 * отбрасывал, серверный нет, и одна и та же ссылка вела себя по-разному
 * в зависимости от того, как её добавили.
 *
 * «content» стоит здесь не для полноты: у Аудиомании обзор техники живёт
 * по /content/intellektual-okruzhayuscego-zvuka-denon-avr-2803/, и разбор
 * уверенно признавал его карточкой — с ценой 500 долларов, взятой из фразы
 * «в аппаратах за $500-700». Желание «Интеллектуал окружающего звука»
 * за пятьсот долларов человек не заказывал.
 *
 * Отзывы сюда не входят намеренно: у Лабиринта карточка книги живёт
 * по адресу /reviews/goods/1016323/, и вычеркнуть её было бы ошибкой.
 *
 * Страницы про людей и события добавлены позже и по той же причине.
 * У Республики /authors/84549 — страница автора, и разбор приносил с неё
 * желание «Анчал Малхотра» за 1 470 ₽: имя человека и цена случайной его
 * книги, без единого предупреждения. У «Подписных изданий» /events/374894 —
 * встреча с писателем. Ни то, ни другое не вещь, которую дарят.
 *
 * Замер по обоим корпусам: расширение задело ровно те же четыре страницы,
 * что и раньше, и все четыре — разделы. Книгу Лабиринта /books/938861/
 * и его же /reviews/goods/… не тронуло.
 */
const АДРЕС_ЧТИВА =
  /\/(blog|article|articles|news|novosti|stati|press|pressroom|journal|content|authors?|avtory?|persons?|events?|meropriyatiya|afisha)\//i;

/**
 * Кусок пути, КОНЧАЮЩИЙСЯ словом про чтиво: «lg-magazine», «nash-blog».
 *
 * Магазины называют свой журнал по себе: у LG статья живёт по адресу
 * /ru/lg-magazine/products/irgy-razuma-v-novom-prilozhenii-smart-tv,
 * и разбор приносил желание «Игры разума в новом приложении Smart TV».
 * Отдельным куском слова там нет, поэтому список выше молчал.
 *
 * «magazine» с конечной «e» — и это не описка: русское «магазин» пишется
 * без неё, и правило со словом «magazin» вычеркнуло бы половину адресов
 * вида /magazin/tovar/123.
 */
const КОНЧАЕТСЯ_ЧТИВОМ = /[-_](magazine|blog|journal|zhurnal|news|press)(\/|$)/i;

/**
 * Поддомен, которым магазин обозначает не витрину, а свою базу знаний.
 *
 * Поймано живьём 08.08.2026 на короткой ссылке Ozon: `s.ozon.ru/VkKwpMV`
 * разворачивается в `seller-edu.ozon.ru/kontrol-kachestva/dokumenty-na-brend` —
 * статью учебного центра продавца. В пути там нет ни одного слова про чтиво,
 * поэтому обе приметы выше молчали, и человек слышал «похоже, это раздел
 * каталога, а не карточка товара — проверьте название и цену». Слова уводили
 * не туда вдвойне: раздела каталога там нет, а «проверьте название и цену»
 * обещает, что карточка почти верна и надо лишь поправить поля.
 *
 * Слово ищем в ПОДДОМЕНЕ, а не во всём хосте, и метку `www` пропускаем.
 * Иначе магазин с именем `news.ru` или `academy.ru` был бы вычеркнут целиком,
 * вместе со своими карточками: собственное имя площадки ничего не говорит
 * о том, что лежит по конкретному адресу, — говорит только приставка к нему.
 *
 * Метку разбираем по дефисам: у Ozon это `seller-edu`, у других встречается
 * `help-center`, `mag-blog`. Целиком такую метку не перечислить, а её часть —
 * ровно то слово, которым площадка и назвала свою базу знаний.
 *
 * `shop`, `market`, `store`, `catalog` сюда не входят и входить не могут —
 * за ними как раз товар и живёт.
 *
 * Замер по обоим корпусам: из 106 карточек описи и 27 не-товарных страниц ни
 * одна не задета — таких поддоменов там нет ни у кого.
 */
const ЯВНОЕ_ЧТИВО = /^(help|support|docs|faq|wiki|knowledge|community|forum|blog|journal|magazine|zhurnal)$/i;

/**
 * Слова, которые называют чтиво, только СТОЯ ГОЛОВОЙ метки — целой меткой или
 * её последним куском.
 *
 * Разделение не из осторожности вообще, а из-за конкретных вещей, которые
 * иначе были бы оболганы. В русских магазинах «edu», «school», «press» бывают
 * ОПРЕДЕЛЕНИЕМ к товару, а не именем базы знаний: `edu-igrushki` — обучающие
 * игрушки, `school-forma` — школьная форма, `press-formy` — пресс-формы, и
 * пресс для сока тоже вещь. Во всех трёх слово стоит ПЕРВЫМ и определяет
 * существительное за ним. У базы знаний наоборот: `seller-edu`, `shop-news` —
 * слово стоит головой, а перед ним уточнение, чья это база.
 *
 * Поэтому `edu` и `news` судим только головой метки, а `school`, `education`
 * и `academy` не судим вовсе: у первых голова разделяет надёжно, у вторых
 * даже головой встречается товар («Академия» — и издательство, и линейка
 * бутс). Цена ошибки тут несимметрична: пропущенная статья стоит человеку
 * лишнего взгляда, а ложная тревога на честной карточке — доверия ко всем
 * следующим предупреждениям.
 */
const ГОЛОВА_ЧТИВА = /^(edu|news|press|learn)$/i;

/** Обозначил ли магазин поддоменом, что это его база знаний, а не витрина. */
function поддоменГоворитЭтоЧтиво(хост: string): boolean {
  const метки = хост.split('.');
  // Последние две метки — имя самой площадки, судить по ним нельзя.
  const приставки = метки.slice(0, Math.max(0, метки.length - 2));
  return приставки.some((метка) => {
    if (метка.toLowerCase() === 'www') return false;
    const куски = метка.split('-');
    const голова = куски[куски.length - 1] ?? '';
    return куски.some((часть) => ЯВНОЕ_ЧТИВО.test(часть)) || ГОЛОВА_ЧТИВА.test(голова);
  });
}

/** Обозначил ли магазин адресом, что это чтиво, а не вещь. */
export function адресГоворитЭтоЧтиво(адрес: string | URL | null | undefined): boolean {
  if (!адрес) return false;
  try {
    const разобран = typeof адрес === 'string' ? new URL(адрес) : адрес;
    return (
      АДРЕС_ЧТИВА.test(разобран.pathname) ||
      КОНЧАЕТСЯ_ЧТИВОМ.test(разобран.pathname) ||
      поддоменГоворитЭтоЧтиво(разобран.hostname)
    );
  } catch {
    return false;
  }
}

/**
 * Ссылки-перевалки: настоящий адрес лежит у них прямо в параметре.
 *
 * Так выглядит ссылка, скопированная из ВКонтакте или из письма: сеть
 * подменяет адрес своим, чтобы посчитать переход, а куда вести — пишет
 * в параметре. Развернуть такую можно без единого запроса в сеть, в отличие
 * от сокращателей вроде clck.ru, за которыми надо ходить.
 *
 * Зачем это нам. Человек делится товаром из ленты, и в поле ссылки уезжает
 * `vk.com/away.php?to=https%3A%2F%2Fshop.ru%2Fp%2F333`. Разбор честно идёт
 * по этому адресу и получает страницу «Вы покидаете ВКонтакте» — ни названия,
 * ни цены, ни фотографии. А в вишлисте остаётся ссылка, которая через год
 * может и вовсе перестать вести куда надо.
 *
 * Список нарочно короткий и точный: только те перевалки, у которых цель
 * лежит в известном параметре. Гадать «в каком-нибудь параметре есть http»
 * нельзя — под это правило попадут поиск, сравнение цен и настоящие адреса
 * с картинками внутри параметров.
 */
const ПЕРЕВАЛКИ: ReadonlyArray<{ хост: RegExp; путь?: RegExp; параметр: string }> = [
  { хост: /^(m\.|away\.)?vk\.com$/i, путь: /^\/away\.php$/i, параметр: 'to' },
  { хост: /^vk\.cc$/i, параметр: 'to' },
  { хост: /^l\.facebook\.com$/i, путь: /^\/l\.php$/i, параметр: 'u' },
  { хост: /^(www\.)?google\.[a-z.]+$/i, путь: /^\/url$/i, параметр: 'q' },
  { хост: /^clck\.yandex\.ru$/i, параметр: 'url' },
  { хост: /^out\.mail\.ru$/i, параметр: 'url' },
  { хост: /^r\.mail\.ru$/i, параметр: 'url' },
];

/** Разворачивает ссылку-перевалку. Обычную ссылку возвращает как есть. */
export function развернутьПереход(адрес: string): string {
  let разобран: URL;
  try {
    разобран = new URL(адрес);
  } catch {
    return адрес;
  }

  for (const перевалка of ПЕРЕВАЛКИ) {
    if (!перевалка.хост.test(разобран.hostname)) continue;
    if (перевалка.путь && !перевалка.путь.test(разобран.pathname)) continue;

    const цель = разобран.searchParams.get(перевалка.параметр);
    if (!цель) continue;
    try {
      const внутри = new URL(цель);
      // Только http и https: перевалка с javascript: внутри — это уже не товар.
      if (внутри.protocol !== 'http:' && внутри.protocol !== 'https:') continue;
      // Перевалка внутри перевалки встречается: ВК внутри почты.
      return развернутьПереход(внутри.toString());
    } catch {
      continue;
    }
  }

  return адрес;
}


/**
 * Ссылка ведёт на файл, а не на страницу.
 *
 * Человек копирует не только адреса страниц. Из контекстного меню браузера
 * легко скопировать «адрес изображения», из отладчика — путь к таблице
 * стилей. Разбор такую ссылку честно скачивал, не находил ничего и называл
 * желание именем файла: «I868297.jpg», «Site.min.0765a7ba7fbf02a40170171c0».
 *
 * Сказать об этом прямо полезнее, чем разбирать: человек сразу поймёт,
 * что скопировал не то, и вернётся за нужной ссылкой. Молчаливая же попытка
 * оставляет его с непонятным желанием и мыслью, что приложение сломалось.
 *
 * Расширения перечислены поимённо. Правило «есть точка в последнем куске»
 * было бы короче и съело бы `/product/kreslo.html` и `/catalog/tovar.php` —
 * а это обычные страницы товаров.
 */
const АДРЕС_ФАЙЛА =
  /\.(css|js|mjs|json|xml|txt|jpe?g|png|gif|webp|svg|ico|avif|bmp|pdf|zip|rar|7z|gz|tar|mp[34]|mov|avi|mkv|woff2?|ttf|otf|eot|doc|docx|xls|xlsx|ppt|pptx|csv|apk|exe|dmg)$/i;

/** Ведёт ли ссылка на файл, а не на страницу товара. */
export function адресВедётНаФайл(адрес: string | URL | null | undefined): boolean {
  if (!адрес) return false;
  try {
    const разобран = typeof адрес === 'string' ? new URL(адрес) : адрес;
    return АДРЕС_ФАЙЛА.test(разобран.pathname);
  } catch {
    return false;
  }
}
